import type { ReactNode } from 'react';
import { Icon, Logo, StatusDot, useSystemTheme } from '@acs/ui';
import { LanguageSelect, useI18n } from '../../i18n/index.js';

/**
 * The split screen every signed-out screen wears (design template "Portal —
 * Sign-in"): the card on the left, the product's promise on the right.
 *
 * Shared since doc 88 added forgot-password, verify-email and the email action
 * page — four screens that must not drift apart in chrome or legal links.
 */
export function AuthShell({ title, lede, children }: { title: string; lede?: ReactNode; children: ReactNode }) {
  const { t } = useI18n();
  // Signed-out screens follow the device's light/dark, with no control: the
  // owner's own choice lives in the account menu and applies once signed in.
  useSystemTheme();
  return (
    <div className="auth">
      <div className="auth__form">
        <div className="auth__top">
          {/* Deliberately NOT a link to `/landing`. While the product is in
              internal testing the marketing page has to stay unreachable from
              the app: no route into it means nobody arrives on a page whose
              prices are not yet agreed. Restore the link when it launches. */}
          <Logo />
          <div className="auth__prefs">
            {/* Chosen here, before the account exists, so sign-up and its
                verification email already speak the owner's language. */}
            <LanguageSelect />
          </div>
        </div>

        <div className="auth__center">
          <div className="auth__card">
            <h1 className="auth__title">{title}</h1>
            {lede && <p className="auth__lede">{lede}</p>}
            {children}
          </div>
        </div>

        <div className="auth__legal">
          <a href="/privacy">{t('auth.privacy')}</a>
          <a href="/terms">{t('auth.terms')}</a>
          <a href="/impressum">{t('auth.impressum')}</a>
          <span>{t('auth.dataEu')}</span>
        </div>
      </div>

      <div className="auth__aside">
        <div className="auth__aside-inner">
          <span className="auth__pill">
            <StatusDot tone="success" pulse />
            {t('auth.pill')}
          </span>
          <h2 className="auth__headline">{t('auth.headline')}</h2>
          <p className="auth__sub">{t('auth.sub')}</p>
          <div className="auth__proof">
            <span className="auth__proof-icon">
              <Icon name="phone" size={20} strokeWidth={2.2} />
            </span>
            <div>
              <div className="auth__proof-title">{t('auth.proofTitle')}</div>
              <div className="auth__proof-sub">{t('auth.proofSub')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
